import { createSignal, For, Show } from 'solid-js';
import { useNavigate } from '@solidjs/router';
import Text from '../components/Text';
import { useApi, isOk } from '../api';
import { useToast } from '../components/MessageBox';
import { useTranslation } from '../hooks/useTranslation';
import { ItemPresets } from '../models/ItemPresets';
import styles from './NewDropConfig.module.css';
import ItemStackView from '../components/ItemStackView';
import LocationPicker from '../components/LocationPicker';
import { fromIdentifier } from '../services/itemRefAdapters';
import { Citybuild } from '../models/Citybuild';

import deDE from '../de_DE.json';

interface DropConfigPayload {
    name: string;
    description: string;
    location: string;
    citybuild: Citybuild;
    itemsToDrop: string[];
    everythingExceptRepairable: boolean;
}

const getCitybuildLabel = (citybuild: Citybuild): string => {
    const labels = {
        [Citybuild.CB1]: 'CB1', [Citybuild.CB2]: 'CB2', [Citybuild.CB3]: 'CB3', [Citybuild.CB4]: 'CB4',
        [Citybuild.CB5]: 'CB5', [Citybuild.CB6]: 'CB6', [Citybuild.CB7]: 'CB7', [Citybuild.CB8]: 'CB8',
        [Citybuild.CB9]: 'CB9', [Citybuild.CB10]: 'CB10', [Citybuild.CB11]: 'CB11', [Citybuild.CB12]: 'CB12',
        [Citybuild.CB13]: 'CB13', [Citybuild.CB14]: 'CB14', [Citybuild.CB15]: 'CB15', [Citybuild.CB16]: 'CB16',
        [Citybuild.CB17]: 'CB17', [Citybuild.CB18]: 'CB18', [Citybuild.CB19]: 'CB19', [Citybuild.CB20]: 'CB20',
        [Citybuild.CB21]: 'CB21', [Citybuild.CB22]: 'CB22'
    };
    return labels[citybuild as keyof typeof labels] || 'UNKNOWN';
};

export default function NewDropConfig() {
    const navigate = useNavigate();
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();
    const translations = deDE as any;
    
    const [formData, setFormData] = createSignal<DropConfigPayload>({
        name: '',
        description: '',
        location: '',
        citybuild: Citybuild.CB1,
        itemsToDrop: [],
        everythingExceptRepairable: false
    });

    const [isSubmitting, setIsSubmitting] = createSignal(false);
    const [showItemSelector, setShowItemSelector] = createSignal(false);
    const [itemSearchQuery, setItemSearchQuery] = createSignal('');

    const handleSubmit = async (e: Event) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            const response = await api.minecraftConfig.createDropConfig(formData() as never);

            if (!isOk(response.data)) {
                toast?.error(t('NEW_DROP_CONFIG_CREATE_FAILED'));
                throw new Error('Failed to create drop config');
            }

            toast?.info(t('NEW_DROP_CONFIG_CREATED'));
            navigate(`/dropconfigs`);
        } catch (error) {
            toast?.error(t('NEW_DROP_CONFIG_CREATE_ERROR'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const addItem = (itemId: string) => {
        if (!formData().itemsToDrop.includes(itemId)) {
            setFormData(prev => ({
                ...prev,
                itemsToDrop: [...prev.itemsToDrop, itemId]
            }));
        }
        // Do not close the modal after adding an item
        setItemSearchQuery('');
    };

    const removeItem = (itemId: string) => {
        setFormData(prev => ({
            ...prev,
            itemsToDrop: prev.itemsToDrop.filter(id => id !== itemId)
        }));
    };

    const getItemName = (itemId: string): string => {
        return translations?.[itemId] || itemId;
    };

    const getFilteredItems = () => {
        if (!translations) return [];
        const selected = formData().itemsToDrop;
        const query = itemSearchQuery().toLowerCase();
        return Object.keys(translations)
            .filter(itemId =>
                !selected.includes(itemId) &&
                (itemId.toLowerCase().includes(query) || translations[itemId].toLowerCase().includes(query))
            )
            .slice(0, 50);
    };

    const applyPreset = (presetName: string) => {
        const preset = ItemPresets.getPresetByName(presetName);
        if (preset) {
            setFormData(prev => ({
                ...prev,
                itemsToDrop: [...new Set([...prev.itemsToDrop, ...preset.items])]
            }));
        }
    };

    const clearItems = () => {
        setFormData(prev => ({
            ...prev,
            itemsToDrop: []
        }));
    };

    return (
        <div class={styles.container}>
            <div class={styles.header}>
                <h1><Text val="CREATE_NEW_DROP_CONFIG" /></h1>
                <p class={styles.subtitle}>
                    <Text val="CONFIGURE_AUTOMATIC_ITEM_DROPPING_FOR_MINECRAFT" />
                </p>
            </div>

            <form onSubmit={handleSubmit} class={styles.form}>
                <div class={styles.grid}>
                    <div class={styles.section}>
                        <h2><Text val="BASIC_INFORMATION" /></h2>
                        
                        <div class={styles.field}>
                            <label class={styles.label}>
                                <Text val="NAME" /> *
                            </label>
                            <input
                                type="text"
                                class={styles.input}
                                value={formData().name}
                                onInput={(e) => setFormData(prev => ({ ...prev, name: e.currentTarget.value }))}
                                placeholder={t('DROP_CONFIG_NAME_PLACEHOLDER')}
                                required
                            />
                        </div>

                        <div class={styles.field}>
                            <label class={styles.label}>
                                <Text val="DESCRIPTION" />
                            </label>
                            <textarea
                                class={styles.textarea}
                                value={formData().description}
                                onInput={(e) => setFormData(prev => ({ ...prev, description: e.currentTarget.value }))}
                                placeholder={t('DROP_CONFIG_DESCRIPTION_PLACEHOLDER')}
                                rows={4}
                            />
                        </div>
                    </div>

                    <div class={styles.section}>
                        <h2><Text val="CONFIGURATION" /></h2>
                        
                        <div class={styles.field}>
                            <label class={styles.label}>
                                <Text val="LOCATION" /> *
                            </label>
                            <LocationPicker
                                value={formData().location}
                                onChange={(next) => setFormData(prev => ({ ...prev, location: next }))}
                            />
                        </div>

                        <div class={styles.field}>
                            <label class={styles.label}>
                                <Text val="CITYBUILD" /> *
                            </label>
                            <select
                                class={styles.select}
                                value={formData().citybuild}
                                onChange={(e) => setFormData(prev => ({ ...prev, citybuild: parseInt(e.currentTarget.value) as Citybuild }))}
                            >
                                <For each={Object.keys(Citybuild).filter(key => isNaN(Number(key)))}>
                                    {(key) => (
                                        <option value={Citybuild[key as keyof typeof Citybuild]}>
                                            {getCitybuildLabel(Citybuild[key as keyof typeof Citybuild])}
                                        </option>
                                    )}
                                </For>
                            </select>
                        </div>

                        <div class={styles.field}>
                            <label class={styles.checkboxLabel}>
                                <input
                                    type="checkbox"
                                    class={styles.checkbox}
                                    checked={formData().everythingExceptRepairable}
                                    onChange={(e) => setFormData(prev => ({ ...prev, everythingExceptRepairable: e.currentTarget.checked }))}
                                />
                                <span class={styles.checkboxText}>
                                    <Text val="EVERYTHING_EXCEPT_REPAIRABLE" />
                                </span>
                            </label>
                            <div class={styles.fieldHelp}>
                                <Text val="EVERYTHING_EXCEPT_REPAIRABLE_HELP" />
                            </div>
                        </div>
                    </div>
                </div>

                <div class={styles.section}>
                    <div class={styles.sectionHeader}>
                        <h2>
                            <Text val="ITEMS_TO_DROP" /> ({formData().itemsToDrop.length})
                        </h2>
                        <button
                            type="button"
                            class={`${styles.addItemButton} ${formData().everythingExceptRepairable ? styles.disabled : ''}`}
                            onClick={() => setShowItemSelector(true)}
                            disabled={formData().everythingExceptRepairable}
                            style={formData().everythingExceptRepairable ? 'pointer-events: none; opacity: 0.5; cursor: not-allowed;' : ''}
                        >
                            <i class="fa-solid fa-plus"></i>
                            <Text val="ADD_ITEM" />
                        </button>
                    </div>

                    <div class={`${styles.presets} ${formData().everythingExceptRepairable ? styles.disabled : ''}`} style={formData().everythingExceptRepairable ? 'pointer-events: none; opacity: 0.5;' : ''}>
                        <div class={styles.presetsHeader}>
                            <Text val="ITEM_PRESETS" />:
                        </div>
                        <div class={styles.presetButtons}>
                            <For each={ItemPresets.ALL_PRESETS}>
                                {(preset) => (
                                    <button
                                        type="button"
                                        class={styles.presetButton}
                                        onClick={() => applyPreset(preset.name)}
                                        disabled={formData().everythingExceptRepairable}
                                    >
                                        <i class={preset.icon}></i>
                                        <Text val={preset.name} />
                                    </button>
                                )}
                            </For>
                            <button
                                type="button"
                                class={styles.clearButton}
                                onClick={clearItems}
                                disabled={formData().everythingExceptRepairable}
                            >
                                <i class="fa-solid fa-trash"></i>
                                <Text val="CLEAR_ALL" />
                            </button>
                        </div>
                    </div>

                    <Show when={formData().itemsToDrop.length > 0} fallback={
                        <div class={styles.emptyItems}>
                            <i class="fa-solid fa-cube"></i>
                            <p><Text val="NO_ITEMS_SELECTED_YET" /></p>
                            <span><Text val="CLICK_ADD_ITEM_TO_GET_STARTED" /></span>
                        </div>
                    }>
                        <div class={`${styles.itemsGrid} ${formData().everythingExceptRepairable ? styles.disabled : ''}`} style={formData().everythingExceptRepairable ? 'pointer-events: none; opacity: 0.5;' : ''}>
                            <For each={formData().itemsToDrop}>
                                {(itemId) => (
                                    <div class={styles.itemCard}>
                                        <div class={styles.itemIcon}>
                                            <ItemStackView stack={fromIdentifier(itemId)} />
                                        </div>
                                        <div class={styles.itemInfo}>
                                            <div class={styles.itemName}>{getItemName(itemId)}</div>
                                            <div class={styles.itemId}>{itemId}</div>
                                        </div>
                                        <Show when={!formData().everythingExceptRepairable}>
                                            <button 
                                                type="button"
                                                class={styles.removeItemButton}
                                                onClick={() => removeItem(itemId)}
                                            >
                                                <i class="fa-solid fa-times"></i>
                                            </button>
                                        </Show>
                                    </div>
                                )}
                            </For>
                        </div>
                    </Show>
                </div>

                <div class={styles.actions}>
                    <button 
                        type="button" 
                        class={styles.cancelButton}
                        onClick={() => navigate('/dropconfigs')}
                    >
                        <Text val="CANCEL" />
                    </button>
                    <button 
                        type="submit" 
                        class={styles.submitButton}
                        disabled={isSubmitting() || !formData().name || !formData().location}
                    >
                        <Show when={isSubmitting()} fallback={
                            <>
                                <i class="fa-solid fa-save"></i>
                                <Text val="CREATE_DROP_CONFIG" />
                            </>
                        }>
                            <i class="fa-solid fa-spinner fa-spin"></i>
                            <Text val="CREATING" />
                        </Show>
                    </button>
                </div>
            </form>

            <Show when={showItemSelector()}>
                <div class={styles.modal} onClick={() => setShowItemSelector(false)}>
                    <div class={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div class={styles.modalHeader}>
                            <h3><Text val="SELECT_ITEMS" /></h3>
                            <button 
                                type="button"
                                class={styles.modalClose}
                                onClick={() => setShowItemSelector(false)}
                            >
                                <i class="fa-solid fa-times"></i>
                            </button>
                        </div>
                        
                        <div class={styles.searchBox}>
                            <i class="fa-solid fa-search"></i>
                            <input
                                type="text"
                                placeholder={t('SEARCH_ITEMS_PLACEHOLDER')}
                                value={itemSearchQuery()}
                                onInput={(e) => setItemSearchQuery(e.currentTarget.value)}
                                class={styles.searchInput}
                            />
                        </div>

                        <div class={styles.itemsList}>
                            <For each={getFilteredItems()}>
                                {(itemId) => (
                                    <div 
                                        class={styles.selectableItem}
                                        onClick={() => addItem(itemId)}
                                    >
                                        <div class={styles.itemIcon}>
                                            <ItemStackView stack={fromIdentifier(itemId)} />
                                        </div>
                                        <div class={styles.itemInfo}>
                                            <div class={styles.itemName}>{getItemName(itemId)}</div>
                                            <div class={styles.itemId}>{itemId}</div>
                                        </div>
                                    </div>
                                )}
                            </For>
                        </div>
                    </div>
                </div>
            </Show>
        </div>
    );
}
